import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import _ from "lodash";
import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";

import {
  DATA_SET_KEYS,
  accumulateSubOpponentsData,
  calcNormalizedOpponentData,
  computeNormalizedSummarizedDataSet,
  currencyModifier,
  mapSummarizedDataSet,
  summarizeStatistic,
} from "@/lib/statistics-analytic-utils";
import {
  Currency,
  colorPool,
} from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { PeriodStatisticsDto } from "@/shared/api";

export type NumberArrEntry = [string, number[]];

export function BarChart({ data }: { data: PeriodStatisticsDto[] }) {
  const summarizedStatistic = useMemo(() => summarizeStatistic(data), [data]);
  const [currency, setCurrency] = useState<Currency>("Chips");

  const labels = useMemo(
    () => summarizedStatistic.map((i) => i.period),
    [summarizedStatistic]
  );

  const chartData = useMemo(() => {
    const heroNetWins: number[] = [];
    const heroRakes: number[] = [];
    const winnerNetWins: number[] = [];
    const winnerRakes: number[] = [];
    const losersRakes: number[] = [];
    const subOppsWinnerNetWins: Map<string, number[]> = new Map();
    const subOppsWinnerRakes: Map<string, number[]> = new Map();
    const subOppsLosersRakes: Map<string, number[]> = new Map();

    summarizedStatistic.forEach((i) => {
      const oppData = calcNormalizedOpponentData(i);

      switch (currency) {
        case "Chips":
          heroNetWins.push(i.hero_gross_profit - i.hero_rake);
          heroRakes.push(i.hero_rake);
          winnerNetWins.push(oppData.winnerGrossProfit - oppData.winnerRake);
          winnerRakes.push(oppData.winnerRake);
          losersRakes.push(oppData.loserRake);
          break;
        case "BB":
          heroNetWins.push(i.hero_gross_profit_bb - i.hero_rake_bb);
          heroRakes.push(i.hero_rake_bb);
          winnerNetWins.push(oppData.winnerGrossProfitBB - oppData.winnerRakeBB);
          winnerRakes.push(oppData.winnerRakeBB);
          losersRakes.push(oppData.loserRakeBB);
          break;
        case "Usd":
          heroNetWins.push(i.hero_gross_profit_usd - i.hero_rake_usd);
          heroRakes.push(i.hero_rake_usd);
          winnerNetWins.push(oppData.winnerGrossProfitUsd - oppData.winnerRakeUsd);
          winnerRakes.push(oppData.winnerRakeUsd);
          losersRakes.push(oppData.loserRakeUsd);
          break;
      }

      accumulateSubOpponentsData(
        i,
        currency,
        subOppsWinnerNetWins,
        subOppsWinnerRakes,
        subOppsLosersRakes
      );
    });

    return {
      heroNetWins,
      heroRakes,
      winnerNetWins,
      winnerRakes,
      losersRakes,
      subOppsWinnerNetWins,
      subOppsWinnerRakes,
      subOppsLosersRakes,
    };
  }, [currency, summarizedStatistic]);

  return (
    <>
      <FormControl sx={{ minWidth: 320, marginBottom: 2 }} size="small">
        <InputLabel id="demo-simple-select-label">Currency</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={currency}
          label="Currency"
          onChange={(e) => setCurrency(e.target.value as Currency)}
        >
          <MenuItem value="Chips">Chips</MenuItem>
          <MenuItem value="BB">BB</MenuItem>
          <MenuItem value="Usd">Usd</MenuItem>
        </Select>
      </FormControl>

      <Bar
        data={{
          labels,
          datasets: [
            {
              label: "Hero Net Win",
              data: chartData.heroNetWins,
              backgroundColor: "#5ecfc0",
            },
            {
              label: "Hero Rake",
              data: chartData.heroRakes,
              backgroundColor: "#ff2159",
            },
            {
              label: "Winners Net Win",
              data: chartData.winnerNetWins,
              backgroundColor: "#cf5ecb",
            },
            ...chartData.subOppsWinnerNetWins.entries().map(([key, data]) => ({
              label: key,
              data,
              backgroundColor: colorPool,
            })),
            {
              label: "Winners Rake",
              data: chartData.winnerRakes,
              backgroundColor: "#fff066",
            },
            ...chartData.subOppsWinnerRakes.entries().map(([key, data]) => ({
              label: key,
              data,
              backgroundColor: colorPool,
            })),
            {
              label: "Losers Rake",
              data: chartData.losersRakes,
              backgroundColor: "#ffa666",
            },
            ...chartData.subOppsLosersRakes.entries().map(([key, data]) => ({
              label: key,
              data,
              backgroundColor: colorPool,
            })),
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "bottom" },
          },
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
              ticks: {
                callback: function (value) {
                  return value.toLocaleString(); // красиво отображать 100,000
                },
              },
            },
          },
        }}
      />
    </>
  );
}
